/**
 * lib/islamicRegister.ts
 * ------------------------------------------------------------------
 * THE ISLAMIC LESSON REGISTER (the school, 2026-10-21): an Islamic teacher
 * opens it and meets the group they should be teaching NOW.
 *
 *  - The lessons are the TIMETABLE's (timetableSlots): Islamic groups sit
 *    inside split slots beside the class's other group (Quraan beside
 *    Reading in Std 1KM; Islamic Studies girls and Quraan boys G1/G2 beside
 *    GC in Forms 1-2). A group is ISLAMIC by its subject (`isIslamicSubject`).
 *  - EACH LESSON KEEPS ITS OWN BELL. An Islamic teacher can take Std 5 and
 *    Form 1 on one day, and Std 5 (upper primary) and Form 1 (secondary) ring
 *    at different times, so a lesson's time comes from the schedule of its
 *    class's division, never one schedule for the teacher's day.
 *  - WHO IS ON THE LIST: the pupils of the slot's classes who are in an
 *    Islamic GROUP drawing on those classes (reportIslamicGroups), narrowed by
 *    the stream's gender ("girls", "boys G1"). Pupils who do not take Islamic
 *    (they are in Reading) are not listed. Where one slot has two groups of
 *    the same gender (boys G1 and G2) the timetable cannot say who is in
 *    which, so the teacher chooses their pupils once (`IslamicLessonSet`).
 *  - The register itself is an ordinary lesson register (timetableAttendance,
 *    lib/lessonAttendance.ts), so it sits with every other lesson's marks.
 *
 * Pure: no Firestore here.
 * ------------------------------------------------------------------ */
import type { Division } from '@/lib/divisions';
import type { SchoolClock } from '@/lib/liveTimetable';
import { parseRange } from '@/lib/liveTimetable';
import { slotGroups, type Day, type ScheduleSettings, type SlotGroup, type TimetableSlot } from '@/lib/timetable';

export const ISLAMIC_LESSON_SETS = 'reportIslamicLessonSets';

/**
 * Quraan, Islamic Studies, Fiqh & Aqaaid, Islamic History & Hadeeth, Hifz &
 * Duas, Akhlaaq, Adiyaat.
 *
 * NOT ARABIC (2026-10-26). Every Arabic lesson on the timetable is the
 * ACADEMIC subject (arabic / subj_jp_arabic / subj_up_arabic: Apa Nadia,
 * Sheikh Hassan, Apa Abeer), and those teachers also hold the Islamic role, so
 * reading "Arabic" as Islamic put their academic Arabic lessons in their
 * Islamic Register. Arabic is still an Islamic Department SUBJECT for marks
 * and reports (lib/islamic.ts); it just has no Islamic lesson on the timetable.
 */
export function isIslamicSubject(label?: string | null): boolean {
  return /qur'?a+n|islam|fiqh|aqa+id|hadee?th|hifz|akhla+q|adiya+t|duas?\b/i.test(String(label ?? ''));
}

/** The division a class NAME belongs to, when the class record does not say. */
export function divisionOfClassName(name: string): Division {
  const s = name.trim().toLowerCase();
  if (s.startsWith('reception') || s.startsWith('nursery')) return 'preprimary';
  const std = /(?:std|standard)\s*(\d)/.exec(s);
  if (std) return Number(std[1]) <= 3 ? 'juniorprimary' : 'upperprimary';
  return 'secondary';
}

/** "girls" / "boys G1" -> 'F' / 'M'; anything else -> null (both). */
export function streamGender(stream?: string | null): 'M' | 'F' | null {
  const s = String(stream ?? '').toLowerCase();
  if (/\bgirls?\b/.test(s)) return 'F';
  if (/\bboys?\b/.test(s)) return 'M';
  return null;
}
const pupilGender = (g?: string) => (String(g ?? '').toUpperCase().startsWith('F') ? 'F' : String(g ?? '').toUpperCase().startsWith('M') ? 'M' : null);

export interface IslamicLesson {
  slot: TimetableSlot;
  groupIndex: number;
  group: SlotGroup;
  division: Division;
  /** Minutes past midnight, from the division's bell. */
  from: number;
  to: number;
  time: string;
}

/** Every Islamic lesson this teacher has on one day, in time order. */
export function teacherIslamicLessons(
  teacherId: string,
  slots: TimetableSlot[],
  schedules: Partial<Record<Division, ScheduleSettings>>,
  day: Day,
  divisionOfClass: (className: string) => Division = divisionOfClassName,
): IslamicLesson[] {
  const out: IslamicLesson[] = [];
  for (const slot of slots) {
    if (slot.day !== day) continue;
    slotGroups(slot).forEach((group, groupIndex) => {
      if (!(group.teacherIds ?? []).includes(teacherId) || !isIslamicSubject(group.subjectLabel)) return;
      const cls = (group.classes?.length ? group.classes : slot.classes)[0] ?? '';
      const division = divisionOfClass(cls);
      const bell = schedules[division]?.days?.[day] ?? [];
      const first = bell.find((p) => p.period === slot.period_start);
      const last = bell.find((p) => p.period === slot.period_end);
      const a = first ? parseRange(first.time) : null;
      const b = last ? parseRange(last.time) : null;
      if (!a || !b) return;
      out.push({ slot, groupIndex, group, division, from: a[0], to: b[1], time: `${hm(a[0])}–${hm(b[1])}` });
    });
  }
  return out.sort((x, y) => x.from - y.from);
}
const hm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** The lesson to open on: the one running now, else the next today, else the last. */
export function lessonToOpen(lessons: IslamicLesson[], clock: Pick<SchoolClock, 'minutes'> | null): { lesson: IslamicLesson | null; when: 'now' | 'next' | 'earlier' | null } {
  if (!lessons.length) return { lesson: null, when: null };
  if (!clock) return { lesson: lessons[0], when: null };
  const now = lessons.find((l) => clock.minutes >= l.from && clock.minutes < l.to);
  if (now) return { lesson: now, when: 'now' };
  const next = lessons.find((l) => l.from > clock.minutes);
  if (next) return { lesson: next, when: 'next' };
  return { lesson: lessons[lessons.length - 1], when: 'earlier' };
}

export interface IslamicGroupLike { id: string; name: string; academicClassIds: string[]; gender?: 'M' | 'F' | null; studentIds: string[] }
export interface PupilLike { id: string; fullName?: string; currentClass: string; gender?: string }

/**
 * Which of a slot's same-gender groups a pupil is in, keyed by the group's
 * TEACHER(S): the stream labels cannot be trusted to differ (Std 7's two
 * girls' Quraan groups are both "girls"), and a teacher keeps their group
 * across the week's lessons.
 */
export const streamKey = (g: { teacherIds?: string[]; streamLabel?: string | null }) =>
  (g.teacherIds ?? []).join(',') || String(g.streamLabel ?? '');

/** One set, for a slot whose same-gender groups the timetable cannot tell apart. */
export interface IslamicLessonSet {
  id: string;
  /** studentId -> the streamKey of their group. */
  assignments: Record<string, string>;
  updatedAt?: string;
  updatedBy?: string;
}
/** A set belongs to the classes and the gender, so it holds across the week's slots. */
export function lessonSetId(slot: TimetableSlot, gender: 'M' | 'F' | null): string {
  return `${[...slot.classes].sort().join('_')}__${gender ?? 'all'}`.replace(/[^A-Za-z0-9_-]+/g, '-');
}

export interface IslamicRoster<P extends PupilLike> {
  pupils: P[];
  /** The Islamic groups the list was drawn from. */
  groups: string[];
  /** Other groups of the same gender share the slot, and nobody has said who is in which. */
  needsSet: boolean;
  /** Every candidate for the set, when choosing one. */
  candidates: P[];
  setId: string;
}

export function islamicRosterFor<P extends PupilLike>(
  slot: TimetableSlot, groupIndex: number, pupils: P[], classIdsByName: Map<string, string>,
  islamicGroups: IslamicGroupLike[], sets: Record<string, IslamicLessonSet | undefined>,
): IslamicRoster<P> {
  const groups = slotGroups(slot);
  const group = groups[groupIndex];
  const names = (group?.classes?.length ? group.classes : slot.classes) ?? [];
  const classIds = new Set(names.map((n) => classIdsByName.get(n)).filter(Boolean) as string[]);
  const gender = streamGender(group?.streamLabel);
  const matched = islamicGroups.filter((g) => g.academicClassIds.some((c) => classIds.has(c)) && (!gender || !g.gender || g.gender === gender));
  const inGroups = new Set(matched.flatMap((g) => g.studentIds));
  const candidates = pupils
    .filter((p) => classIds.has(p.currentClass) && inGroups.has(p.id) && (!gender || !pupilGender(p.gender) || pupilGender(p.gender) === gender))
    .sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? ''));
  const twins = groups.filter((g, i) => i !== groupIndex && isIslamicSubject(g.subjectLabel) && streamGender(g.streamLabel) === gender);
  const setId = lessonSetId(slot, gender);
  const set = sets[setId];
  const stream = group ? streamKey(group) : '';
  if (!twins.length) return { pupils: candidates, groups: matched.map((g) => g.name), needsSet: false, candidates, setId };
  if (!set) return { pupils: candidates, groups: matched.map((g) => g.name), needsSet: true, candidates, setId };
  return { pupils: candidates.filter((p) => set.assignments[p.id] === stream), groups: matched.map((g) => g.name), needsSet: false, candidates, setId };
}
